import type { PageContext } from "./types/messaging.js";

/**
 * Extract visible text content from the DOM, filtering out non-content elements
 */
function extractPageText(): string {
  if (typeof document === "undefined") return "";

  // Clone the body to avoid modifying the actual DOM
  const bodyClone = document.body.cloneNode(true) as HTMLElement;

  // Remove script, style, noscript tags and hidden elements
  const unwantedSelectors = [
    "script",
    "style",
    "noscript",
    "iframe",
    "[style*='display: none']",
    "[style*='display:none']",
    "[hidden]",
    ".yak-chat-widget",
  ];

  for (const selector of unwantedSelectors) {
    const elements = bodyClone.querySelectorAll(selector);
    for (const el of elements) {
      el.remove();
    }
  }

  // Get text content and clean it up
  let text = bodyClone.textContent || bodyClone.innerText || "";

  // Normalize whitespace: replace multiple spaces/newlines with single space
  text = text.replace(/\s+/g, " ").trim();

  // Limit to reasonable size (100KB max)
  const maxLength = 100000;
  if (text.length > maxLength) {
    text = `${text.substring(0, maxLength)}... [truncated]`;
  }

  return text;
}

/**
 * Extract page context including URL, title, and visible text content
 */
export function extractPageContext(): PageContext {
  if (typeof window === "undefined") {
    return {
      url: "",
      title: "",
      text: "",
      timestamp: Date.now(),
    };
  }
  return {
    url: window.location.href,
    title: document.title,
    text: extractPageText(),
    timestamp: Date.now(),
  };
}

/**
 * Debounce function to limit how often a function is called
 */
export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}
